/**
 * Controls hint — a small glass card that teaches the scene's navigation, then
 * gets out of the way.
 *
 * It describes what THIS scene actually does, on THIS device:
 *   walk  + mouse → drag to look around, WASD or scroll to move
 *   walk  + touch → joystick to move, swipe to look
 *   orbit + mouse → drag to rotate, scroll to zoom
 *   orbit + touch → swipe to rotate, pinch to zoom
 * A rotating scene must never be told how to walk, so the mode is passed in
 * by mount() from the controller it actually built.
 *
 * Each glyph ANIMATES its gesture (the cursor slides, the chevrons scroll,
 * the joystick knob circles) — a static icon says what, the motion says how.
 * Animations are CSS-driven (viewer.css) and stop under
 * prefers-reduced-motion.
 *
 * Placement: bottom-centre — the one region every other piece of chrome
 * leaves alone (info top-left, toolbar top-right, branding bottom-left,
 * joystick bottom-right). On phones it lifts above the joystick.
 *
 * Lifecycle: fades in after the reveal (rows staggered). With `probes` it is
 * a PRACTICE CARD: each row watches the actual camera state and checks itself
 * off when the user really performs that gesture (drag → view rotated,
 * scroll → position/zoom changed); once both pass, a short "all done" beat
 * and it leaves. Without probes it falls back to leaving on the first
 * navigation input. Either way a timeout and the × remain as exits.
 */

import { resolveLang } from './lang';

export type HintMode = 'walk' | 'orbit';

export interface HintProbe {
    /** Sample the watched value(s) — e.g. [yaw, pitch] or [x, z]. */
    get: () => number[];
    /** Euclidean delta vs. the card-shown baseline that counts as done. */
    threshold: number;
    /**
     * Gesture attribution: "has the TAUGHT input actually been performed?"
     * Without it, any camera change ticks the row — click-to-glide checked
     * off "scroll to move" and orbit auto-rotation checked off "drag to
     * rotate" with zero input. A row completes only when armed() is true AND
     * the metric moved: the user did the gesture and saw its effect.
     */
    armed?: () => boolean;
}

export interface ControlsHintOptions {
    parent: HTMLElement;
    mode: HintMode;
    /** Force touch/mouse copy. Defaults to feature detection. */
    touch?: boolean;
    /**
     * Scene has a camera tour, whose ▶ button also sits bottom-centre — lift
     * the card above it instead of covering it.
     */
    aboveTour?: boolean;
    /**
     * Practice probes by MEANING (look = view rotated, move = travelled /
     * zoomed). The card maps them onto its rows itself — row order differs
     * per device (phones teach move first), so positional probes would wire
     * the wrong gesture to the wrong row. When set, rows check off as the
     * user actually performs each gesture and the card leaves once both pass.
     */
    probes?: { look: HintProbe; move: HintProbe };
    /**
     * Replace the taught rows outright — for a scheme the mode × device matrix
     * above has no cell for (the A/B/C/D experiment's hover-look). Each row
     * names the practice probe that watches it. The caller owns device
     * detection: rows passed on a phone teach whatever they say.
     */
    rows?: HintRow[];
    /** Delay before the card appears (ms) — lets the reveal land first. */
    delay?: number;
    /** Auto-dismiss after this long on screen (ms). */
    linger?: number;
}

type Row = { glyph: string; label: string };
/** A taught row: glyph markup, label, and which probe (by meaning) ticks it. */
export type HintRow = Row & { probe: 'look' | 'move' };

// Minimal line glyphs, 18px, currentColor — same weight as the toolbar icons.
const svg = (d: string, extra = '') =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}${extra}</svg>`;

// Glyph notes: at ~20px a mouse silhouette reads as a "0" (with a stem, a
// microphone) — so zoom is a magnifier and scroll is chevrons. The dot in the
// swipe glyph is the fingertip; without it the arrow reads as "tab". The
// ch-anim-* groups are animated from viewer.css.
const GLYPH = {
    dragLook: svg('<g class="ch-anim-slide"><path d="M7 4l5.5 13.5 1.9-5.4 5.3-2z"/></g>'),
    scrollMove: svg('<g class="ch-anim-scroll"><path d="M8 8l4-4 4 4"/><path d="M8 16l4 4 4-4"/></g>'),
    joystick: svg('<circle cx="12" cy="12" r="8.5"/><g class="ch-anim-orbit"><circle cx="12" cy="9.6" r="2.7" fill="currentColor" stroke="none"/></g>'),
    swipe: svg('<g class="ch-anim-slide"><circle cx="6.5" cy="12" r="2.3" fill="currentColor" stroke="none"/><path d="M11 12h7"/><path d="M14.8 8.4l3.7 3.6-3.7 3.6"/></g>'),
    rotate: svg('<g class="ch-anim-rock"><path d="M20 12a8 8 0 1 1-2.3-5.6"/><path d="M20 4v4h-4"/></g>'),
    zoom: svg('<g class="ch-anim-pulse"><circle cx="10.5" cy="10.5" r="6.2"/><path d="M15.2 15.2L20 20"/><path d="M8 10.5h5"/><path d="M10.5 8v5"/></g>'),
    pinch: svg('<g class="ch-anim-pinch-a"><path d="M9.5 9.5L4.5 4.5"/><path d="M4.5 9V4.5H9"/></g><g class="ch-anim-pinch-b"><path d="M14.5 14.5l5 5"/><path d="M19.5 15v4.5H15"/></g>')
};

// The viewer has no i18n layer of its own; the marketing site auto-detects
// language, so a Spanish visitor opening a demo should not get English hints.
// Every label is a full "gesture → effect" phrase — a bare noun ("Move") made
// people guess which input it referred to.
type Copy = {
    dragLook: string; scrollMove: string;
    joyMove: string; swipeLook: string;
    dragRotate: string; scrollZoom: string;
    swipeRotate: string; pinchZoom: string;
    dismiss: string;
};
const COPY: Record<'en' | 'es' | 'ca', Copy> = {
    en: {
        dragLook: 'Drag to look around', scrollMove: 'WASD or scroll to move',
        joyMove: 'Joystick to move', swipeLook: 'Swipe to look around',
        dragRotate: 'Drag to rotate', scrollZoom: 'Scroll to zoom',
        swipeRotate: 'Swipe to rotate', pinchZoom: 'Pinch to zoom',
        dismiss: 'Dismiss'
    },
    es: {
        dragLook: 'Arrastra para mirar', scrollMove: 'WASD o scroll para moverte',
        joyMove: 'Joystick para moverte', swipeLook: 'Desliza para mirar',
        dragRotate: 'Arrastra para girar', scrollZoom: 'Scroll para acercar',
        swipeRotate: 'Desliza para girar', pinchZoom: 'Pellizca para acercar',
        dismiss: 'Cerrar'
    },
    ca: {
        dragLook: 'Arrossega per mirar', scrollMove: 'WASD o scroll per moure’t',
        joyMove: 'Joystick per moure’t', swipeLook: 'Llisca per mirar',
        dragRotate: 'Arrossega per girar', scrollZoom: 'Scroll per apropar',
        swipeRotate: 'Llisca per girar', pinchZoom: 'Pessiga per apropar',
        dismiss: 'Tancar'
    }
};

// Checkmark that replaces a row's glyph the moment its gesture is performed.
const CHECK = svg('<path d="M5.5 12.5l4.3 4.3L18.5 8"/>');

export class ControlsHint {

    private root: HTMLDivElement;

    private rowEls: HTMLElement[] = [];

    private timer: number | null = null;

    private pollTimer: number | null = null;

    /** Per-ROW probes, already mapped from the semantic {look, move} pair. */
    private probes: HintProbe[] | null = null;

    private baseline: number[][] = [];

    private doneCount = 0;

    private disposed = false;

    private detach: Array<() => void> = [];

    constructor({ parent, mode, touch, aboveTour = false, probes, rows: customRows, delay = 900, linger = 12000 }: ControlsHintOptions) {
        const isTouch = touch ?? (matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window);
        // Language of the PAGE that embeds us (the site passes ?lang=), not
        // the browser's — a Catalan browser on the English page must get
        // English hints, and vice versa.
        const t = COPY[resolveLang()];

        // Desktop walk teaches two rows: drag to look, WASD or scroll to
        // move. Click-to-walk still works but isn't taught — two lines is the
        // whole budget. Each row carries its semantic probe
        // key so practice mode watches the RIGHT camera property per row
        // (phones teach move first, so positional wiring would cross them).
        const defaultRows: HintRow[] = mode === 'walk'
            ? isTouch
                ? [{ glyph: GLYPH.joystick, label: t.joyMove, probe: 'move' }, { glyph: GLYPH.swipe, label: t.swipeLook, probe: 'look' }]
                : [{ glyph: GLYPH.dragLook, label: t.dragLook, probe: 'look' }, { glyph: GLYPH.scrollMove, label: t.scrollMove, probe: 'move' }]
            : isTouch
                ? [{ glyph: GLYPH.swipe, label: t.swipeRotate, probe: 'look' }, { glyph: GLYPH.pinch, label: t.pinchZoom, probe: 'move' }]
                : [{ glyph: GLYPH.rotate, label: t.dragRotate, probe: 'look' }, { glyph: GLYPH.zoom, label: t.scrollZoom, probe: 'move' }];
        const rows = customRows ?? defaultRows;
        if (probes) this.probes = rows.map(r => probes[r.probe]);

        this.root = document.createElement('div');
        this.root.className = aboveTour ? 'ollo-hints ollo-hints--above-tour' : 'ollo-hints';
        this.root.setAttribute('role', 'note');
        rows.forEach((r, i) => {
            if (i > 0) {
                const sep = document.createElement('span');
                sep.className = 'ollo-hints-sep';
                sep.setAttribute('aria-hidden', 'true');
                this.root.appendChild(sep);
            }
            const row = document.createElement('span');
            row.className = 'ollo-hints-row';
            row.innerHTML = `<span class="ollo-hints-glyph">${r.glyph}</span><span class="ollo-hints-label">${r.label}</span>`;
            this.root.appendChild(row);
            this.rowEls.push(row);
        });

        const close = document.createElement('button');
        close.className = 'ollo-hints-close';
        close.type = 'button';
        close.setAttribute('aria-label', t.dismiss);
        close.innerHTML = svg('<path d="M6 6l12 12M18 6L6 18"/>');
        close.addEventListener('click', (e) => { e.stopPropagation(); this.hide(); });
        this.root.appendChild(close);

        parent.appendChild(this.root);

        // Appear after the reveal so it doesn't collide with the loading fade.
        this.timer = window.setTimeout(() => {
            if (this.disposed) return;
            this.root.classList.add('visible');
            if (this.probes) {
                // Practice mode: the card waits for the user to actually do
                // both gestures, so it earns a longer stay. Timeout remains
                // for people who ignore it entirely.
                this.timer = window.setTimeout(() => this.hide(), Math.max(linger, 30000));
                this.baseline = this.probes.map(p => p.get());
                this.pollTimer = window.setInterval(() => this.poll(), 250);
            } else {
                this.timer = window.setTimeout(() => this.hide(), linger);
                this.armDismissOnUse();
            }
        }, delay);
    }

    /** Check each unfinished row against its probe's baseline. */
    private poll(): void {
        if (!this.probes) return;
        this.probes.forEach((probe, i) => {
            const row = this.rowEls[i];
            if (!row || row.classList.contains('done')) return;
            const now = probe.get();
            const base = this.baseline[i];
            if (probe.armed && !probe.armed()) {
                // The taught gesture hasn't happened — whatever moved the
                // camera (click-glide, auto-rotation) doesn't count. Re-base
                // so that drift can't pre-pay the threshold either.
                this.baseline[i] = now;
                return;
            }
            const d = Math.hypot(...now.map((v, k) => v - (base[k] ?? 0)));
            if (d > probe.threshold) this.markDone(i);
        });
    }

    /** The user performed row i's gesture — check it off; leave once all pass. */
    private markDone(i: number): void {
        const row = this.rowEls[i];
        row.classList.add('done');
        const glyph = row.querySelector('.ollo-hints-glyph');
        if (glyph) glyph.innerHTML = CHECK;
        this.doneCount++;
        if (this.probes && this.doneCount >= this.probes.length) {
            if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
            if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
            this.root.classList.add('complete');
            // A short beat so the second checkmark is actually seen.
            this.timer = window.setTimeout(() => this.hide(), 1100);
        }
    }

    /**
     * The hint has done its job the moment the user navigates — so leave on the
     * first input that actually drives the camera. Deliberately NOT plain
     * pointermove: on desktop, hover IS the look control, so the pointer is
     * moving constantly and the card would vanish before it could be read.
     */
    private armDismissOnUse(): void {
        const go = () => this.hide();
        const keyGo = (e: KeyboardEvent) => {
            if (/^(w|a|s|d|arrowup|arrowdown|arrowleft|arrowright)$/i.test(e.key)) this.hide();
        };
        window.addEventListener('pointerdown', go, { passive: true });
        window.addEventListener('wheel', go, { passive: true });
        window.addEventListener('keydown', keyGo);
        this.detach.push(
            () => window.removeEventListener('pointerdown', go),
            () => window.removeEventListener('wheel', go),
            () => window.removeEventListener('keydown', keyGo)
        );
    }

    hide(): void {
        if (this.disposed) return;
        if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
        if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
        for (const fn of this.detach) fn();
        this.detach = [];
        this.root.classList.remove('visible');
        // Let the fade finish before pulling it out of the DOM.
        window.setTimeout(() => this.root.remove(), 400);
    }

    dispose(): void {
        if (this.timer !== null) clearTimeout(this.timer);
        if (this.pollTimer !== null) clearInterval(this.pollTimer);
        for (const fn of this.detach) fn();
        this.detach = [];
        this.disposed = true;
        this.root.remove();
    }
}
